import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { styled } from 'styled-components';
import { useHeaderHeight } from '../hooks/useHeaderHeight.ts';
import {
  postGameResultReplayAPI,
  PostGameResultReplayResponse,
} from '../services/apis/gameDataAPI.ts';

const DEFAULT_LANGUAGE = 'en';

function safeJsonParse<T>(value: string | undefined, fallback: T): T {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

function toErrorText(error: unknown): string {
  if (!error) return 'Unknown error';
  if (typeof error === 'string') return error;
  if (typeof error !== 'object') return String(error);

  const anyError = error as {
    status?: number;
    statusText?: string;
    data?: { message?: string; error?: string };
    message?: string;
  };

  return (
    anyError.data?.message ||
    anyError.data?.error ||
    anyError.statusText ||
    anyError.message ||
    'Request failed'
  );
}

function formatDateTimeYYYYMMDDHHmmss(date: Date) {
  const pad2 = (n: number) => String(n).padStart(2, '0');
  const yyyy = date.getFullYear();
  const mm = pad2(date.getMonth() + 1);
  const dd = pad2(date.getDate());
  const hh = pad2(date.getHours());
  const mi = pad2(date.getMinutes());
  const ss = pad2(date.getSeconds());
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
}

function addSecondsToYYYYMMDDHHmmss(value: string, secondsToAdd: number) {
  // Expected: "YYYY-MM-DD HH:mm:ss"
  const [datePart, timePart] = value.split(' ');
  if (!datePart || !timePart) return null;

  const [y, m, d] = datePart.split('-').map((x) => Number(x));
  const [hh, mi, ss] = timePart.split(':').map((x) => Number(x));
  if (
    !Number.isFinite(y) ||
    !Number.isFinite(m) ||
    !Number.isFinite(d) ||
    !Number.isFinite(hh) ||
    !Number.isFinite(mi) ||
    !Number.isFinite(ss)
  ) {
    return null;
  }

  const dt = new Date(y, m - 1, d, hh, mi, ss);
  if (Number.isNaN(dt.getTime())) return null;

  dt.setSeconds(dt.getSeconds() + secondsToAdd);
  return formatDateTimeYYYYMMDDHHmmss(dt);
}

function formatNumber(value: number | undefined, maxFractionDigits = 10) {
  if (value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: maxFractionDigits,
  }).format(value);
}

function formatPercentPoints(value: number | undefined) {
  if (value === undefined || Number.isNaN(value)) return '—';
  return `${value.toFixed(2)}%`;
}

type CoinPerformanceMap = Record<string, number>;
type WinnerAMap = Record<'1' | '2' | '3', string>;

const ReplayPage = () => {
  const location = useLocation();
  const { headerHeight } = useHeaderHeight();

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const roundid = searchParams.get('roundid')?.trim() || '';
  const transactionid = searchParams.get('transactionid')?.trim() || '';
  const hash = searchParams.get('hash')?.trim() || '';
  const language = (searchParams.get('language')?.trim() || DEFAULT_LANGUAGE)
    .toLowerCase()
    .slice(0, 5);

  const [data, setData] = useState<PostGameResultReplayResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    setError(null);

    if (!roundid || !transactionid || !hash) {
      setError(
        'Missing required query params. Expected: roundid, transactionid, hash (optional: language).'
      );
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    postGameResultReplayAPI({
      round: roundid,
      transactionid,
      hash,
      language,
    })
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((e) => {
        if (!cancelled) setError(toErrorText(e));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [roundid, transactionid, hash, language]);

  const derived = useMemo(() => {
    if (!data) return null;

    const start = safeJsonParse<Record<string, number>>(data.round.start, {});
    const end = safeJsonParse<Record<string, number>>(data.round.end, {});
    const performance = safeJsonParse<{ A?: CoinPerformanceMap }>(
      data.round.performance,
      {}
    );
    const performanceA = performance.A || {};

    const winnerA = safeJsonParse<WinnerAMap>(data.round.winnerA, {
      1: '',
      2: '',
      3: '',
    });

    const coinKeys = Array.from(
      new Set([...Object.keys(start), ...Object.keys(end), ...Object.keys(performanceA)])
    ).sort((a, b) => a.localeCompare(b));

    const coinRows = coinKeys
      .map((coin) => {
      const startValue = start[coin];
      const endValue = end[coin];
      const perfRaw = performanceA[coin];

      // Derive actual percentage points from start/end, when possible.
      const derivedPerfPoints =
        startValue !== undefined && endValue !== undefined && startValue !== 0
          ? ((endValue - startValue) / startValue) * 100
          : undefined;

      // Backend formats vary. Some return ratio (e.g. -0.0014 for -0.14%),
      // others return percent points already (e.g. -0.14 for -0.14%).
      // Pick the scaling that matches the derived start/end delta best.
      const perfPoints =
        perfRaw === undefined || Number.isNaN(perfRaw)
          ? undefined
          : derivedPerfPoints === undefined || Number.isNaN(derivedPerfPoints)
            ? // No reliable derived value → default to percent-points (matches provided backend examples)
              perfRaw
            : Math.abs(perfRaw - derivedPerfPoints) <=
                Math.abs(perfRaw * 100 - derivedPerfPoints)
              ? perfRaw
              : perfRaw * 100;

      return {
        coin,
        start: startValue,
        end: endValue,
        perfRaw,
        derivedPerfPoints,
        perfPoints,
      };
      })
      .sort((a, b) => {
        const aPerf = a.perfPoints;
        const bPerf = b.perfPoints;

        if (aPerf === undefined && bPerf === undefined) {
          return a.coin.localeCompare(b.coin);
        }
        if (aPerf === undefined) return 1;
        if (bPerf === undefined) return -1;

        // Best performing first
        if (bPerf !== aPerf) return bPerf - aPerf;
        return a.coin.localeCompare(b.coin);
      });

    return {
      start,
      end,
      performanceA,
      winnerA,
      coinRows,
    };
  }, [data]);

  const normalizedWon = useMemo(() => {
    const won = data?.gamebetuser?.won;
    return typeof won === 'number' && Number.isFinite(won) && won > 0 ? won : 0;
  }, [data?.gamebetuser?.won]);

  return (
    <Wrapper $headerHeight={headerHeight}>
      <Container>
        <HeaderRow>
          <Title>Replay</Title>
          <RightMeta>
            <MetaLine>
              <MetaLabel>Round</MetaLabel>
              <MetaValue>#{roundid || '—'}</MetaValue>
            </MetaLine>
            <MetaLine>
              <MetaLabel>Language</MetaLabel>
              <MetaValue>{language || '—'}</MetaValue>
            </MetaLine>
          </RightMeta>
        </HeaderRow>

        {isLoading && (
          <Card>
            <CardTitle>Loading result…</CardTitle>
            <CardText>This may take a moment.</CardText>
          </Card>
        )}

        {error && (
          <Card $variant="error">
            <CardTitle>Couldn’t load replay</CardTitle>
            <CardText>{error}</CardText>
            <Hint>
              Example:{' '}
              <Code>
                /replay/?roundid=503&transactionid=16&hash=...&language=en
              </Code>
            </Hint>
          </Card>
        )}

        {data && derived && (
          <Grid>
            <Card>
              <CardTitle>Bet summary</CardTitle>
              <SummaryGrid>
                <SummaryItem>
                  <SummaryLabel>Transaction ID</SummaryLabel>
                  <SummaryValue>{data.gamebetuser.transactionid}</SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Bet</SummaryLabel>
                  <SummaryValue>{data.gamebetuser.bet}</SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Amount</SummaryLabel>
                  <SummaryValue>{formatNumber(data.gamebetuser.amount, 2)}</SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Won</SummaryLabel>
                  <WonValue $won={normalizedWon > 0}>
                    {formatNumber(normalizedWon, 2)}
                  </WonValue>
                </SummaryItem>
              </SummaryGrid>
            </Card>

            <Card>
              <CardTitle>Round timing</CardTitle>
              <SummaryGrid>
                <SummaryItem>
                  <SummaryLabel>Start</SummaryLabel>
                  <SummaryValue>{data.round.start_at}</SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>End</SummaryLabel>
                  <SummaryValue>
                    {addSecondsToYYYYMMDDHHmmss(data.round.start_at, 30) ??
                      data.round.end_at}
                  </SummaryValue>
                </SummaryItem>
              </SummaryGrid>
            </Card>

            <Card $spanAll>
              <CardTitle>Prices & performance</CardTitle>
              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <Th align="right">#</Th>
                      <Th align="left">Coin</Th>
                      <Th align="right">Start</Th>
                      <Th align="right">End</Th>
                      <Th align="right">Performance</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {derived.coinRows.map((row, idx) => (
                      <Tr key={row.coin}>
                        <Td align="right">{idx + 1}</Td>
                        <Td align="left">
                          <CoinText>{row.coin}</CoinText>
                          {derived.winnerA?.['1']?.toLowerCase?.() ===
                            row.coin.toLowerCase() && (
                            <WinnerBracket>({data.round.winnerB})</WinnerBracket>
                          )}
                        </Td>
                        <Td align="right">{formatNumber(row.start)}</Td>
                        <Td align="right">{formatNumber(row.end)}</Td>
                        <Td align="right">
                          <Perf $negative={(row.perfPoints ?? 0) < 0}>
                            {formatPercentPoints(row.perfPoints)}
                          </Perf>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>
            </Card>
          </Grid>
        )}
      </Container>
    </Wrapper>
  );
};

const Wrapper = styled.main<{ $headerHeight: number }>`
  width: 100%;
  min-height: 100vh;
  @supports (min-height: 100dvh) {
    min-height: 100dvh;
  }

  padding: ${({ theme }) => theme.spacing(20, 16, 32, 16)};
  padding-top: ${({ theme, $headerHeight }) =>
    `calc(${theme.spacing(24)} + ${$headerHeight}px)`};

  ${({ theme }) => theme.getUpMedia('md')} {
    padding-top: ${({ theme }) => theme.spacing(28)};
  }
`;

const Container = styled.div`
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(16)};
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(12)};
  flex-wrap: wrap;
`;

const Title = styled.h1`
  font-weight: 700;
  font-size: ${({ theme }) => theme.responsive(28)};
  line-height: 1.1;
`;

const RightMeta = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(12)};
  flex-wrap: wrap;
`;

const MetaLine = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MetaLabel = styled.span`
  opacity: 0.7;
  font-size: ${({ theme }) => theme.responsive(12)};
`;

const MetaValue = styled.span`
  font-weight: 600;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing(12)};

  ${({ theme }) => theme.getUpMedia('md')} {
    grid-template-columns: 1fr 1fr;
  }
`;

const Card = styled.section<{ $variant?: 'error'; $spanAll?: boolean }>`
  background: ${({ theme }) => theme.getColor('bgCards')};
  border: 1px solid
    ${({ theme, $variant }) =>
      $variant === 'error' ? theme.getColor('error') : theme.getColor('textDark')};
  border-radius: ${({ theme }) => theme.responsive(16)};
  padding: ${({ theme }) => theme.spacing(14)};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);

  ${({ $spanAll, theme }) =>
    $spanAll
      ? `
    ${theme.getUpMedia('md')} {
      grid-column: 1 / -1;
    }
  `
      : ''}
`;

const CardTitle = styled.h2`
  font-size: ${({ theme }) => theme.responsive(16)};
  font-weight: 700;
  margin-bottom: ${({ theme }) => theme.spacing(10)};
`;

const CardText = styled.p`
  opacity: 0.85;
`;

const Hint = styled.p`
  margin-top: ${({ theme }) => theme.spacing(10)};
  opacity: 0.85;
`;

const Code = styled.code`
  padding: 2px 6px;
  border-radius: 8px;
  background: ${({ theme }) => theme.getColor('textDark')};
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing(10)};

  ${({ theme }) => theme.getUpMedia('md')} {
    grid-template-columns: 1fr 1fr;
  }
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SummaryLabel = styled.span`
  opacity: 0.7;
  font-size: ${({ theme }) => theme.responsive(12)};
`;

const SummaryValue = styled.span`
  font-weight: 600;
  word-break: break-word;
`;

const WonValue = styled(SummaryValue)<{ $won: boolean }>`
  font-weight: 800;
  color: ${({ theme, $won }) =>
    $won ? theme.getColor('active') : theme.getColor('ratesDown')};
`;

const TableWrapper = styled.div`
  overflow: auto;
  border-radius: ${({ theme }) => theme.responsive(12)};
  border: 1px solid ${({ theme }) => theme.getColor('textDark')};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 520px;
`;

const Th = styled.th<{ align?: 'left' | 'right' }>`
  text-align: ${({ align }) => align || 'left'};
  padding: ${({ theme }) => theme.spacing(10)};
  font-size: ${({ theme }) => theme.responsive(12)};
  opacity: 0.75;
  background: ${({ theme }) => theme.getColor('bgCardsActive')};
  position: sticky;
  top: 0;
`;

const Tr = styled.tr`
  border-top: 1px solid ${({ theme }) => theme.getColor('textDark')};
`;

const Td = styled.td<{ align?: 'left' | 'right' }>`
  text-align: ${({ align }) => align || 'left'};
  padding: ${({ theme }) => theme.spacing(10)};
  font-variant-numeric: tabular-nums;
`;

const CoinText = styled.span`
  font-weight: 700;
  text-transform: uppercase;
`;

const WinnerBracket = styled.span`
  margin-left: 6px;
  font-weight: 800;
  color: ${({ theme }) => theme.getColor('accentSecondary')};
`;

const Perf = styled.span<{ $negative: boolean }>`
  font-weight: 700;
  color: ${({ theme, $negative }) =>
    $negative ? theme.getColor('ratesDown') : theme.getColor('active')};
`;

export { ReplayPage };

